<script lang="ts">
  // BJL-CHECK: Button — the collapse (▼/▲) and close (✕) controls are bjl Buttons in the
  // `text` variant with iconOnly. The sheet shell itself is custom; see
  // artifacts/deviations/Sheet.md (bjl Drawer is a viewport-edge overlay with its own
  // open/close animation and backdrop, and cannot express the shared drag-resizable
  // height that all four tabs animate against).
  import { Button } from '@byjackli-labs/ui/Button'
  import { Colors } from '../ui/colors'
  import { keyboardInset } from '../ui/safeArea'
  import { panelFrame, panelTitle } from '../ui/theme'
  import type { Snippet } from 'svelte'

  /** Handle bar — white at 25%, the same on every sheet in the redesign. */
  const HANDLE = 'rgba(255,255,255,0.25)'

  /**
   * The title row's icon buttons (▼ and ✕). A square, because bjl's Button takes
   * `padding: 8px 18px` from index.css — right for a labelled button, but on a single glyph
   * it buys 36px of dead width, sits ✕ a third of an inch off the panel edge, and opens a
   * gulf between ✕ and anything a page puts beside it. 34px is still a comfortable target.
   */
  const HEADER_ICON =
    'width: 34px; height: 34px; min-width: 34px; min-height: 34px; padding: 0; flex-shrink: 0;'

  interface Props {
    title: string
    open: boolean
    height: number
    dragging: boolean
    /** Minimum visible height (handle + header), owned by App and shared by all sheets. */
    minHeight: number
    onHandleDown: (e: PointerEvent) => void
    onClose: () => void
    /** When set, a ▼/▲ button next to ✕ collapses the sheet to its minimum height
     *  (and back), so the user can set it aside and see the map — same pattern as
     *  the boarded-vehicle panel. */
    onToggleCollapse?: () => void
    /**
     * A control the *page* contributes to the sheet's own title row, immediately left of ✕:
     * for something that is chrome rather than content, like the alerts line search. It
     * arrives as a snippet published upward by the page (see AlertsPage's `onHeaderActions`)
     * because only the page holds the state such a control drives.
     */
    headerActions?: Snippet
    children?: Snippet
  }

  const {
    title, open, height, dragging, minHeight,
    onHandleDown, onClose, onToggleCollapse, headerActions, children,
  }: Props = $props()

  const collapsed = $derived(height <= minHeight + 10)

  /**
   * How much of THIS container's bottom edge the on-screen keyboard covers.
   *
   * `--keyboard-inset` is measured from the bottom of the VIEWPORT, and a sheet lives in the
   * content area, whose bottom edge stops one tab bar short of it — so the tab bar's height
   * comes off, or the sheet lifts a tab bar too far and floats with a strip of map under it.
   * Clamped at 0 so a keyboard shorter than the tab bar (or no keyboard) leaves the sheet
   * exactly where it was. App measures and publishes `--tab-bar-height`.
   */
  const keyboardLift = `max(0px, calc(${keyboardInset()} - var(--tab-bar-height, 0px)))`
</script>

<!-- A bottom sheet: slid off-screen when closed rather than unmounted, so its transform
     animates both ways. All sheets share one drag-resizable height (see App), which is why
     `height` and the drag handler are passed in rather than owned here.

     It stands on the keyboard rather than on the bottom edge, because every text input in
     the app is in one of these (Plan's From/To, Routes' search, the alerts line search) and
     the on-screen keyboard opens over exactly this edge — a rider was typing into a field
     the keyboard was sitting on. Lifting alone is not enough: the sheet is up to 85% of the
     screen tall, so raised by a ~45%-tall keyboard its top would leave the screen and take
     the From/To rows — which are the FIRST thing in the content — with it. So the same lift
     also caps the height, and the sheet's own scroller absorbs the rest. `max-height` is
     transitioned with `height` so the two edges move together instead of the top snapping
     while the bottom eases. -->
<div
  style="
    position: absolute; bottom: {keyboardLift}; left: 0; right: 0;
    height: {height}px;
    max-height: calc(100% - {keyboardLift});
    background-color: {Colors.ground};
    {panelFrame('top')}
    display: flex; flex-direction: column;
    z-index: 201;
    overflow: hidden;
    transform: {open ? 'translateY(0)' : 'translateY(100%)'};
    transition: {dragging
      ? 'none'
      : 'height 0.25s cubic-bezier(0.4,0,0.2,1), max-height 0.25s cubic-bezier(0.4,0,0.2,1),'
        + ' bottom 0.25s cubic-bezier(0.4,0,0.2,1), transform 0.3s cubic-bezier(0.4,0,0.2,1)'};
    will-change: transform, height;
    pointer-events: {open ? 'auto' : 'none'};
  "
>
  <!-- Drag handle: pointer down starts resize -->
  <div
    onpointerdown={onHandleDown}
    role="separator"
    aria-label="Resize {title}"
    style="
      flex-shrink: 0;
      display: flex; justify-content: center;
      padding: 8px 0 0;
      cursor: ns-resize; user-select: none;
      touch-action: none;
    "
  >
    <div style="width: 36px; height: 4px; border-radius: 2px; background-color: {HANDLE};"></div>
  </div>

  <!-- Title row. No rule beneath it: in the redesign the sheet's own filter/content row is
       what follows the title, and a divider there cuts the panel in half visually. -->
  <!-- The 12px gap is a MINIMUM separation, not the usual one: `space-between` already parks
       the title and the controls at opposite edges, so it only bites when a header control
       expands to fill the row (the alerts line search) and would otherwise run straight into
       the last letter of the title. -->
  <div
    style="
      padding: 10px 16px 2px 20px;
      display: flex; align-items: center; justify-content: space-between; gap: 12px;
      flex-shrink: 0;
    "
  >
    <!-- Ellipsizes rather than holding its width: a header control that expands (the alerts
         line search) takes the room it needs from the title, which is the one thing here the
         rider already knows — they just tapped the tab that opened it. -->
    <span
      style="{panelTitle(22)} min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"
    >{title}</span>
    <!-- 6px between the header's controls: enough that a thumb aimed at ✕ can't take the
         control beside it, small enough that they still read as one cluster. -->
    <div
      style="
        display: flex; align-items: center; gap: 6px;
        flex: 1 1 auto; min-width: 0; justify-content: flex-end;
      "
    >
      {#if headerActions}{@render headerActions()}{/if}
      {#if onToggleCollapse}
        <Button
          variant="text"
          size="sm"
          iconOnly
          onclick={onToggleCollapse}
          aria-label={collapsed ? `Expand ${title}` : `Collapse ${title}`}
          style={HEADER_ICON}
        >
          {collapsed ? '▲' : '▼'}
        </Button>
      {/if}
      <Button
        variant="text"
        size="sm"
        iconOnly
        onclick={onClose}
        aria-label="Close {title}"
        style={HEADER_ICON}
      >
        ✕
      </Button>
    </div>
  </div>

  <!-- Content: overflow visible so inner scroll works -->
  <div style="flex: 1; min-height: 0; display: flex; flex-direction: column;">
    {@render children?.()}
  </div>
</div>
